import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { usePdfRenderer } from '../hooks/usePdfRenderer';
import { RENDER_TYPE } from '../types';
import { getPixelRatio, isIOSSafari } from '../utils/common';

interface TocPageProps {
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
    pdfDoc: any;
    pageNum: number;
    onJump?: (pageNum: number) => void;
    scale?: number;
    visible?: boolean;
    mainViewportScale?: number; // 主展示区域的 scale（从 usePdfViewport 获取）
}

export const TocPage = memo((props: TocPageProps) => {
    const { pdfDoc, pageNum, onJump, scale: propScale, visible = true } = props;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);
    const [renderReady, setRenderReady] = useState(false);
    const [calculatedScale, setCalculatedScale] = useState<number | null>(null);
    // 缓存已计算的 scale，避免 visible 切换时重复调用 pdfDoc.getPage
    const calculatedScaleRef = useRef<number | null>(null);

    const fitCanvasToBox = useCallback(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!(container && canvas)) {
            return;
        }

        const boxW = container.clientWidth || 0;
        const boxH = container.clientHeight || 0;
        if (boxW <= 0 || boxH <= 0) {
            return;
        }

        // 重要：不要使用 max-width/max-height 让浏览器"单边裁剪"，否则会破坏宽高比（出现被压扁）
        // 缩略图的等比缩放交给 transform 做统一缩放
        canvas.style.maxWidth = '';
        canvas.style.maxHeight = '';

        // offsetWidth/offsetHeight 不包含 transform，适合用于计算"未缩放前"的布局尺寸
        const cw = canvas.offsetWidth || 0;
        const ch = canvas.offsetHeight || 0;
        if (cw <= 0 || ch <= 0) {
            return;
        }

        const ratioW = boxW / cw;
        const ratioH = boxH / ch;
        const ratio = Math.min(1, ratioW, ratioH);

        canvas.style.transformOrigin = 'center center';
        if (ratio < 1) {
            canvas.style.transform = `scale(${ratio})`;
        } else {
            canvas.style.transform = '';
        }
    }, []);

    const scheduleFit = useCallback(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        } else {
            // continue
        }
        rafRef.current = requestAnimationFrame(() => {
            // 再等一帧，确保 usePdfRenderer 写入 canvas.style.width/height 已经生效
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                fitCanvasToBox();
            });
        });
    }, [fitCanvasToBox]);

    const handleRenderSuccess = useCallback(() => {
        scheduleFit();
        setRenderReady(true);
    }, [scheduleFit]);

    // (pdfDoc, pageNum) 变化时重置缓存，visible 切换不重置（避免重复计算）
    useEffect(() => {
        if (propScale !== undefined) return;
        calculatedScaleRef.current = null;
        setCalculatedScale(null);
    }, [pdfDoc, pageNum, propScale]);

    // 每个 (pdfDoc, pageNum) 只计算一次 scale；visible 从 false→true 时直接复用缓存
    useEffect(() => {
        if (propScale !== undefined || !pdfDoc || !visible || calculatedScaleRef.current !== null) {
            return;
        }

        const container = containerRef.current;
        if (!container) {
            return;
        }

        let cancelled = false;

        const checkAndCalculate = () => {
            if (cancelled) return;
            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            if (containerWidth <= 0 || containerHeight <= 0) {
                requestAnimationFrame(checkAndCalculate);
                return;
            }

            pdfDoc
                .getPage(pageNum)
                // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
                .then((page: any) => {
                    if (cancelled) return;
                    const pageViewport = page.getViewport({ scale: 1 });

                    const currentWidth = container.clientWidth;
                    const currentHeight = container.clientHeight;

                    if (currentWidth <= 0 || currentHeight <= 0) {
                        requestAnimationFrame(checkAndCalculate);
                        return;
                    }

                    // TocPage 不支持 tile 分块渲染（usePdfRenderer 的 tileLayout 由 PdfPage 处理）。
                    // 若 scale 过大导致物理像素超出浏览器限制，usePdfRenderer 会进入 tile 路径并
                    // 将主 canvas 设为 display:none，缩略图将永久空白。
                    // 因此必须在这里计算安全上限，确保渲染始终走单 canvas 路径。
                    const dpr = getPixelRatio();
                    const pageW = pageViewport.width;
                    const pageH = pageViewport.height;
                    // 留 15% 滞后裕量，与 usePdfRenderer TILE_HYSTERESIS 对齐
                    const maxSafeScale = isIOSSafari
                        ? // iOS Safari: IOSurface 像素总数上限 16M，取 13.6M 作为安全阈值
                        Math.sqrt(13_600_000 / (pageW * pageH * dpr * dpr))
                        : // 非 iOS: 单边物理像素上限 7680px，取 6528 作为安全阈值
                        6528 / (Math.max(pageW, pageH) * dpr);

                    // 缩略图优先速度：以容器尺寸为上限计算 scale，使 canvas 恰好覆盖容器
                    // 配合 renderPixelRadio=1，canvas 物理尺寸约等于容器 CSS 尺寸（~130×185px）
                    const scaleByW = currentWidth / pageViewport.width;
                    const scaleByH = currentHeight / pageViewport.height;
                    const result = Math.min(scaleByW, scaleByH, maxSafeScale);
                    calculatedScaleRef.current = result;
                    setCalculatedScale(result);
                })
                // biome-ignore lint/suspicious/noExplicitAny: error type
                .catch((error: any) => {
                    console.warn('Failed to calculate scale for TocPage:', error);
                    calculatedScaleRef.current = 0.2;
                    setCalculatedScale(0.2);
                });
        };

        requestAnimationFrame(checkAndCalculate);
        return () => {
            cancelled = true;
        };
    }, [pdfDoc, pageNum, visible, propScale]);

    // 确定最终使用的 scale
    // propScale 由外部明确传入时直接使用；否则等待 calculatedScale 计算完成（初始为 0 跳过渲染，
    // 避免先以 0.2 渲染一次被立即取消，造成 loading 状态残留导致 canvas 长时间不可见）
    const finalScale = propScale !== undefined ? propScale : (calculatedScale ?? 0);

    usePdfRenderer({
        pdfDoc,
        pageNum,
        scale: finalScale,
        renderType: RENDER_TYPE.CANVAS,
        canvasRef,
        visible,
        renderPixelRadio: 1, // 缩略图不需要 DPR 放大，强制 1:1 物理像素
        onRenderSuccess: handleRenderSuccess,
    });

    useEffect(() => {
        if (visible) {
            // continue
        } else {
            return;
        }
        // 可见时触发一次适配（后续尺寸变化由外层布局决定，缩略图不会依赖复杂计算）
        scheduleFit();
    }, [visible, scheduleFit]);

    // 页码变化时重置占位状态；visible 切换不重置（复用已渲染的 canvas 数据，避免闪烁）
    // canvas GPU 内存由虚拟列表卸载组件时自动释放，不在此处手动清零
    useEffect(() => {
        setRenderReady(false);
    }, [pageNum]);

    useEffect(() => {
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            // 卸载时主动将 canvas 尺寸置零，释放 GPU IOSurface 纹理内存
            // （TocPage 未使用 usePdfCanvasManager，需手动处理）
            const canvas = canvasRef.current;
            if (canvas) {
                canvas.width = 0;
                canvas.height = 0;
            }
        };
    }, []);

    return (
        <div ref={containerRef} className="toc-page-container" onClick={() => onJump?.(pageNum)}>
            {/* 骨架占位：首次渲染前显示；renderReady 后永久隐藏，visible 切换不重置 */}
            {visible && !renderReady ? <div className="toc-page-skeleton" /> : null}
            <canvas
                ref={canvasRef}
                style={{
                    display: 'block',
                    // renderReady 后 opacity 始终为 1；背景重渲染期间 canvas 已有数据，不会闪烁
                    opacity: visible && !renderReady ? 0 : 1,
                    willChange: 'transform',
                }}
            />
        </div>
    );
});
